import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Save, Sparkles } from "lucide-react";

export default function AdminPlans() {
  const [pro, setPro] = useState(null);
  const [individual, setIndividual] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/plans/pro`).then((r) => setPro(r.data));
    axios.get(`${API}/admin/plans/individual-monthly`).then((r) => setIndividual(r.data));
  };
  useEffect(load, []);

  const saveProDiscount = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/plans/pro`, { annual_discount_pct: pro.annual_discount_pct });
      toast.success("Annual discount saved");
      load();
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const saveProTiers = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/plans/pro`, { tiers: pro.tiers });
      toast.success("Professional plans saved");
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const saveIndividual = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/plans/individual-monthly`, individual);
      toast.success("Individual Monthly Progress plan saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const patchTier = (idx, patch) => {
    const nextTiers = pro.tiers.map((t, i) => i === idx ? { ...t, ...patch } : t);
    setPro({ ...pro, tiers: nextTiers });
  };

  if (!pro || !individual) return <div className="text-[#6F6B66]">Loading…</div>;

  return (
    <div className="max-w-4xl" data-testid="admin-plans">
      <div className="mb-8">
        <div className="text-[11px] kt-tracking-wider uppercase text-[#6F6B66]">KT Milano</div>
        <h1 className="font-serif text-3xl text-[#383633] mt-1">Plans & pricing</h1>
        <p className="text-sm text-[#6F6B66] mt-2 max-w-2xl">
          Existing paid plans are preserved. Add new professional tiers via the toggles below.
          Annual pricing is derived automatically: <strong>12 × monthly − discount %</strong>.
        </p>
      </div>

      {/* Professional plans */}
      <section className="bg-white border kt-line rounded-2xl p-6 kt-shadow mb-8" data-testid="pro-plans-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl text-[#383633]">Hairtu Professional (Salons)</h2>
          <div className="flex items-center gap-3">
            <Label htmlFor="disc" className="text-xs kt-tracking uppercase text-[#6F6B66]">Annual discount %</Label>
            <Input
              id="disc"
              data-testid="input-annual-discount"
              type="number" min={0} max={40}
              value={pro.annual_discount_pct}
              onChange={(e) => setPro({ ...pro, annual_discount_pct: Number(e.target.value) })}
              className="w-24 h-10 rounded-xl border-[#ECE7DF]"
            />
            <Button size="sm" disabled={saving} data-testid="save-discount-btn"
              onClick={saveProDiscount}
              className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase">
              <Save className="h-3.5 w-3.5 mr-1" /> Save
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {pro.tiers.map((t, i) => (
            <div key={t.id} className="border kt-line rounded-xl p-4 bg-[#F8F6F2]" data-testid={`tier-${t.id}`}>
              <div className="grid sm:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Tier ID</Label>
                  <Input value={t.id} disabled className="mt-1 h-10 rounded-xl bg-white" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Label</Label>
                  <Input value={t.label} onChange={(e) => patchTier(i, { label: e.target.value })}
                    className="mt-1 h-10 rounded-xl bg-white" />
                </div>
                <div>
                  <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Monthly €</Label>
                  <Input type="number" step="0.01" value={t.monthly_price_eur}
                    onChange={(e) => patchTier(i, { monthly_price_eur: Number(e.target.value) })}
                    className="mt-1 h-10 rounded-xl bg-white" />
                </div>
                <div>
                  <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Session limit</Label>
                  <Input placeholder="unlimited"
                    value={t.session_limit ?? ""}
                    onChange={(e) => patchTier(i, { session_limit: e.target.value === "" ? null : Number(e.target.value) })}
                    className="mt-1 h-10 rounded-xl bg-white" />
                </div>
              </div>
              <div className="mt-3 grid sm:grid-cols-[1fr,auto,auto] gap-3 items-center">
                <Input value={t.note || ""} placeholder="Public note (shown to admin only)"
                  onChange={(e) => patchTier(i, { note: e.target.value })}
                  className="h-10 rounded-xl bg-white" />
                <div className="text-[11px] text-[#6F6B66]">
                  Annual: <strong>€{t.annual_price_eur}</strong> ({t.monthly_price_eur * 12} − {pro.annual_discount_pct}%)
                </div>
                <label className="flex items-center gap-2 text-xs text-[#383633] cursor-pointer">
                  <Checkbox checked={!!t.active} onCheckedChange={(v) => patchTier(i, { active: !!v })}
                    className="border-[#6F6B66] data-[state=checked]:bg-[#383633] data-[state=checked]:border-[#383633]" />
                  Active
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={saveProTiers} disabled={saving} data-testid="save-tiers-btn"
            className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase">
            <Save className="h-3.5 w-3.5 mr-1" /> Save professional tiers
          </Button>
        </div>
      </section>

      {/* Individual Monthly Progress */}
      <section className="bg-white border kt-line rounded-2xl p-6 kt-shadow" data-testid="individual-monthly-section">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-[#D8C4A5]" />
          <h2 className="font-serif text-xl text-[#383633]">Individual Monthly Progress (optional opt-in)</h2>
        </div>
        <p className="text-xs text-[#6F6B66] max-w-xl mb-6">
          Voluntary customer opt-in. Never subscribes anyone automatically. Only shown to customers
          when both a price and at least one language of customer-facing terms are set below and
          the plan is toggled <em>active</em>.
        </p>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Monthly price</Label>
            <Input type="number" step="0.01" value={individual.monthly_price_eur}
              data-testid="input-individual-price"
              onChange={(e) => setIndividual({ ...individual, monthly_price_eur: Number(e.target.value) })}
              className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Currency</Label>
            <Input value={individual.currency}
              onChange={(e) => setIndividual({ ...individual, currency: e.target.value })}
              className="mt-1 h-10 rounded-xl" />
          </div>
          <label className="flex items-end gap-2 pb-2 cursor-pointer">
            <Checkbox checked={!!individual.active}
              onCheckedChange={(v) => setIndividual({ ...individual, active: !!v })}
              data-testid="input-individual-active"
              className="border-[#6F6B66] data-[state=checked]:bg-[#383633] data-[state=checked]:border-[#383633]" />
            <span className="text-sm text-[#383633]">Active (visible to customers)</span>
          </label>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Customer terms · EN</Label>
            <Textarea rows={5} value={individual.terms_en}
              data-testid="input-terms-en"
              onChange={(e) => setIndividual({ ...individual, terms_en: e.target.value })}
              placeholder="Explicit opt-in required. Monthly progress check. Cancel anytime from your account."
              className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Customer terms · IT</Label>
            <Textarea rows={5} value={individual.terms_it}
              onChange={(e) => setIndividual({ ...individual, terms_it: e.target.value })}
              placeholder="Adesione esplicita richiesta. Check mensile. Cancellazione in qualunque momento."
              className="mt-1 rounded-xl" />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={saveIndividual} disabled={saving} data-testid="save-individual-btn"
            className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase">
            <Save className="h-3.5 w-3.5 mr-1" /> Save
          </Button>
        </div>
      </section>
    </div>
  );
}
